import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Trophy, Plus, Edit, Trash2, Play, Users, Calendar, Clock,
  Code, Brain, Rocket, Shield, BarChart3, Briefcase, Loader,
  CheckCircle, Award, RefreshCw, Star, Crown, Medal, Eye, X
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const contestTypeIcons = {
  coding: Code,
  ai_challenge: Brain,
  hackathon: Rocket,
  cybersecurity: Shield,
  data_science: BarChart3,
  hiring_challenge: Briefcase
};

const contestTypeColors = {
  coding: '#06B6D4',
  ai_challenge: '#8B5CF6',
  hackathon: '#F59E0B',
  cybersecurity: '#EF4444',
  data_science: '#10B981',
  hiring_challenge: '#EC4899'
};

const CodeArenaContestAdmin = () => {
  const [contests, setContests] = useState([]);
  const [problems, setProblems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingContest, setEditingContest] = useState(null);
  const [selectedContest, setSelectedContest] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);

  const [form, setForm] = useState({
    title: '',
    description: '',
    contest_type: 'coding',
    domains: [],
    start_time: '',
    end_time: '',
    duration_minutes: 120,
    problem_ids: [],
    is_rated: true,
    max_participants: null,
    prizes: { first: '', second: '', third: '' },
    sponsors: [],
    eligibility: ''
  });

  const token = localStorage.getItem('admin_token');

  useEffect(() => {
    fetchContests();
    fetchProblems();
  }, []);

  const fetchContests = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/codearena/contests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContests(res.data.contests || []);
    } catch (err) {
      toast.error('Failed to fetch contests');
    } finally {
      setLoading(false);
    }
  };

  const fetchProblems = async () => {
    try {
      const res = await axios.get(`${API}/admin/codearena/problems`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProblems(res.data.problems || []);
    } catch (err) {
      console.error('Failed to fetch problems:', err);
    }
  };

  const fetchLeaderboard = async (contestId) => {
    try {
      const res = await axios.get(`${API}/admin/codearena/contests/${contestId}/leaderboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeaderboard(res.data.leaderboard || []);
    } catch (err) {
      console.error('Failed to fetch leaderboard:', err);
    }
  };

  const createContest = async () => {
    if (!form.title || !form.start_time || !form.end_time) {
      toast.error('Please fill in all required fields');
      return;
    }
    try {
      await axios.post(`${API}/admin/codearena/contests`, form, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Contest created successfully!');
      setShowModal(false);
      resetForm();
      fetchContests();
    } catch (err) {
      toast.error('Failed to create contest');
    }
  };

  const updateContest = async () => {
    if (!editingContest) return;
    try {
      await axios.put(`${API}/admin/codearena/contests/${editingContest.id}`, form, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Contest updated successfully!');
      setShowModal(false);
      setEditingContest(null);
      resetForm();
      fetchContests();
    } catch (err) {
      toast.error('Failed to update contest');
    }
  };

  const deleteContest = async (contestId) => {
    if (!window.confirm('Are you sure you want to delete this contest?')) return;
    try {
      await axios.delete(`${API}/admin/codearena/contests/${contestId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Contest deleted');
      fetchContests();
    } catch (err) {
      toast.error('Failed to delete contest');
    }
  };

  const endContest = async (contestId) => {
    if (!window.confirm('End this contest and generate certificates?')) return;
    try {
      const res = await axios.post(`${API}/admin/codearena/contests/${contestId}/end`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Contest ended! ${res.data.certificates_generated} certificates generated`);
      fetchContests();
    } catch (err) {
      toast.error('Failed to end contest');
    }
  };

  const seedSampleContest = async () => {
    try {
      const res = await axios.post(`${API}/admin/codearena/seed-contest`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message);
      fetchContests();
    } catch (err) {
      toast.error('Failed to seed contest');
    }
  };

  const resetForm = () => {
    setForm({
      title: '',
      description: '',
      contest_type: 'coding',
      domains: [],
      start_time: '',
      end_time: '',
      duration_minutes: 120,
      problem_ids: [],
      is_rated: true,
      max_participants: null,
      prizes: { first: '', second: '', third: '' },
      sponsors: [],
      eligibility: ''
    });
  };

  const openEditModal = (contest) => {
    setEditingContest(contest);
    setForm({
      title: contest.title || '',
      description: contest.description || '',
      contest_type: contest.contest_type || 'coding',
      domains: contest.domains || [],
      start_time: contest.start_time?.slice(0, 16) || '',
      end_time: contest.end_time?.slice(0, 16) || '',
      duration_minutes: contest.duration_minutes || 120,
      problem_ids: contest.problem_ids || [],
      is_rated: contest.is_rated ?? true,
      max_participants: contest.max_participants || null,
      prizes: contest.prizes || { first: '', second: '', third: '' },
      sponsors: contest.sponsors || [],
      eligibility: contest.eligibility || ''
    });
    setShowModal(true);
  };

  const getContestStatus = (contest) => {
    const now = new Date().toISOString();
    if (now < contest.start_time) return 'upcoming';
    if (now >= contest.start_time && now <= contest.end_time) return 'live';
    return 'ended';
  };

  const domainOptions = [
    'Data Structures & Algorithms',
    'AI / Machine Learning',
    'Web Development',
    'Cybersecurity',
    'Blockchain',
    'Cloud Computing',
    'Data Science'
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <Trophy className="text-purple-600" size={28} />
            Contest Management
          </h2>
          <p className="text-gray-600 mt-1">Create and manage coding competitions</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={seedSampleContest}
            className="flex items-center gap-2 px-4 py-2 bg-amber-100 text-amber-700 rounded-lg hover:bg-amber-200 transition-colors"
          >
            <Star size={18} />
            Seed Sample
          </button>
          <button
            onClick={() => { resetForm(); setEditingContest(null); setShowModal(true); }}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            <Plus size={18} />
            Create Contest
          </button>
        </div>
      </div>

      {/* Contests Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader className="w-8 h-8 text-purple-600 animate-spin" />
        </div>
      ) : contests.length > 0 ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {contests.map((contest, idx) => {
            const status = getContestStatus(contest);
            const Icon = contestTypeIcons[contest.contest_type] || Code;
            const color = contestTypeColors[contest.contest_type] || '#06B6D4';
            
            return (
              <div
                key={idx}
                className={`bg-white rounded-xl shadow-lg border p-5 ${
                  status === 'live' ? 'border-emerald-400' : 'border-gray-200'
                }`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div 
                    className="w-12 h-12 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: `${color}20` }}
                  >
                    <Icon size={24} style={{ color }} />
                  </div>
                  <div className="flex items-center gap-2">
                    {status === 'live' && (
                      <span className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-semibold animate-pulse">
                        LIVE
                      </span>
                    )}
                    {status === 'upcoming' && (
                      <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-semibold">
                        Upcoming
                      </span>
                    )}
                    {status === 'ended' && (
                      <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-semibold">
                        Ended
                      </span>
                    )}
                  </div>
                </div>

                <h3 className="text-lg font-bold text-gray-900 mb-2">{contest.title}</h3>
                <p className="text-gray-500 text-sm mb-4 line-clamp-2">{contest.description}</p>

                <div className="space-y-2 text-sm text-gray-600 mb-4">
                  <div className="flex items-center gap-2">
                    <Calendar size={14} />
                    <span>{new Date(contest.start_time).toLocaleString()}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock size={14} />
                    <span>{contest.duration_minutes} min</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Users size={14} />
                    <span>{contest.participant_count || 0} participants</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Code size={14} />
                    <span>{contest.problem_ids?.length || 0} problems</span>
                  </div>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setSelectedContest(contest);
                      fetchLeaderboard(contest.id);
                    }}
                    className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm"
                  >
                    <Eye size={14} />
                    View
                  </button>
                  <button
                    onClick={() => openEditModal(contest)}
                    className="flex items-center justify-center px-3 py-2 bg-cyan-100 text-cyan-700 rounded-lg hover:bg-cyan-200"
                  >
                    <Edit size={14} />
                  </button>
                  {status === 'live' && (
                    <button
                      onClick={() => endContest(contest.id)}
                      className="flex items-center justify-center px-3 py-2 bg-amber-100 text-amber-700 rounded-lg hover:bg-amber-200"
                      title="End Contest"
                    >
                      <Award size={14} />
                    </button>
                  )}
                  <button
                    onClick={() => deleteContest(contest.id)}
                    className="flex items-center justify-center px-3 py-2 bg-rose-100 text-rose-700 rounded-lg hover:bg-rose-200"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-12 text-center">
          <Trophy className="mx-auto text-gray-300 mb-4" size={48} />
          <h3 className="font-semibold text-gray-900 mb-2">No Contests Yet</h3>
          <p className="text-gray-500">Create your first coding contest or seed a sample.</p>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900">
                {editingContest ? 'Edit Contest' : 'Create Contest'}
              </h3>
              <button
                onClick={() => { setShowModal(false); setEditingContest(null); resetForm(); }}
                className="p-2 text-gray-400 hover:text-gray-600"
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Contest Title *</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  placeholder="Weekly Coding Challenge #1"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 resize-none"
                  placeholder="Contest description..."
                />
              </div>

              {/* Contest Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Contest Type</label>
                <select
                  value={form.contest_type}
                  onChange={(e) => setForm(prev => ({ ...prev, contest_type: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                >
                  <option value="coding">Coding Contest</option>
                  <option value="ai_challenge">AI Challenge</option>
                  <option value="hackathon">Hackathon</option>
                  <option value="cybersecurity">Cybersecurity Challenge</option>
                  <option value="data_science">Data Science Competition</option>
                  <option value="hiring_challenge">Hiring Challenge</option>
                </select>
              </div>

              {/* Date/Time */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Time *</label>
                  <input
                    type="datetime-local"
                    value={form.start_time}
                    onChange={(e) => setForm(prev => ({ ...prev, start_time: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End Time *</label>
                  <input
                    type="datetime-local"
                    value={form.end_time}
                    onChange={(e) => setForm(prev => ({ ...prev, end_time: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  />
                </div>
              </div>

              {/* Duration & Rated */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Duration (minutes)</label>
                  <input
                    type="number"
                    value={form.duration_minutes}
                    onChange={(e) => setForm(prev => ({ ...prev, duration_minutes: parseInt(e.target.value) || 120 }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Max Participants</label>
                  <input
                    type="number"
                    value={form.max_participants || ''}
                    onChange={(e) => setForm(prev => ({ ...prev, max_participants: e.target.value ? parseInt(e.target.value) : null }))}
                    placeholder="Unlimited"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  />
                </div>
              </div>

              {/* Domains */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Domains</label>
                <div className="flex flex-wrap gap-2">
                  {domainOptions.map((domain) => (
                    <button
                      key={domain}
                      type="button"
                      onClick={() => {
                        const domains = form.domains || [];
                        if (domains.includes(domain)) {
                          setForm(prev => ({ ...prev, domains: domains.filter(d => d !== domain) }));
                        } else {
                          setForm(prev => ({ ...prev, domains: [...domains, domain] }));
                        }
                      }}
                      className={`px-3 py-1 rounded-lg text-sm transition-all ${
                        form.domains?.includes(domain)
                          ? 'bg-purple-100 text-purple-700 border border-purple-300'
                          : 'bg-gray-100 text-gray-600 border border-gray-200 hover:bg-gray-200'
                      }`}
                    >
                      {domain}
                    </button>
                  ))}
                </div>
              </div>

              {/* Problems Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Problems</label>
                <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg p-2">
                  {problems.map((problem) => (
                    <label
                      key={problem.id}
                      className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={form.problem_ids.includes(problem.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setForm(prev => ({ ...prev, problem_ids: [...prev.problem_ids, problem.id] }));
                          } else {
                            setForm(prev => ({ ...prev, problem_ids: prev.problem_ids.filter(id => id !== problem.id) }));
                          }
                        }}
                        className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                      />
                      <span className="text-gray-900">{problem.title}</span>
                      <span className={`ml-auto text-xs px-2 py-0.5 rounded ${
                        problem.difficulty === 'easy' ? 'bg-emerald-100 text-emerald-700' :
                        problem.difficulty === 'medium' ? 'bg-amber-100 text-amber-700' :
                        'bg-rose-100 text-rose-700'
                      }`}>
                        {problem.difficulty}
                      </span>
                    </label>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-1">{form.problem_ids.length} problems selected</p>
              </div>

              {/* Prizes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Prizes</label>
                <div className="grid grid-cols-3 gap-2">
                  <input
                    type="text"
                    value={form.prizes?.first || ''}
                    onChange={(e) => setForm(prev => ({ ...prev, prizes: { ...prev.prizes, first: e.target.value } }))}
                    placeholder="1st: ₹10,000"
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500"
                  />
                  <input
                    type="text"
                    value={form.prizes?.second || ''}
                    onChange={(e) => setForm(prev => ({ ...prev, prizes: { ...prev.prizes, second: e.target.value } }))}
                    placeholder="2nd: ₹5,000"
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500"
                  />
                  <input
                    type="text"
                    value={form.prizes?.third || ''}
                    onChange={(e) => setForm(prev => ({ ...prev, prizes: { ...prev.prizes, third: e.target.value } }))}
                    placeholder="3rd: ₹3,000"
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500"
                  />
                </div>
              </div>

              {/* Rated Toggle */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={form.is_rated}
                  onChange={(e) => setForm(prev => ({ ...prev, is_rated: e.target.checked }))}
                  className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                />
                <label className="text-sm text-gray-700">Rated Contest (affects rankings)</label>
              </div>

              {/* Submit */}
              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => { setShowModal(false); setEditingContest(null); resetForm(); }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={editingContest ? updateContest : createContest}
                  className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                >
                  {editingContest ? 'Update Contest' : 'Create Contest'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Contest Modal */}
      {selectedContest && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-3xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900">{selectedContest.title}</h3>
              <button
                onClick={() => { setSelectedContest(null); setLeaderboard([]); }}
                className="p-2 text-gray-400 hover:text-gray-600"
              >
                <X size={20} />
              </button>
            </div>

            <div className="mb-6">
              <h4 className="font-semibold text-gray-900 mb-3">Leaderboard</h4>
              {leaderboard.length > 0 ? (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Rank</th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Name</th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Email</th>
                        <th className="px-4 py-2 text-center text-xs font-semibold text-gray-600">Solved</th>
                        <th className="px-4 py-2 text-right text-xs font-semibold text-gray-600">Score</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {leaderboard.map((entry, idx) => (
                        <tr key={idx} className="hover:bg-gray-50">
                          <td className="px-4 py-2">
                            {entry.rank <= 3 ? (
                              <span className="text-lg">
                                {entry.rank === 1 ? '🥇' : entry.rank === 2 ? '🥈' : '🥉'}
                              </span>
                            ) : (
                              <span className="text-gray-600">{entry.rank}</span>
                            )}
                          </td>
                          <td className="px-4 py-2 font-medium text-gray-900">{entry.full_name || 'Anonymous'}</td>
                          <td className="px-4 py-2 text-gray-500 text-sm">{entry.email || '-'}</td>
                          <td className="px-4 py-2 text-center text-gray-600">{entry.problems_solved || 0}</td>
                          <td className="px-4 py-2 text-right font-bold text-purple-600">{entry.total_score || 0}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">No submissions yet</p>
              )}
            </div>

            <div className="flex justify-end">
              <button
                onClick={() => { setSelectedContest(null); setLeaderboard([]); }}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CodeArenaContestAdmin;
